import {
  Drawer, Box, Typography, Stack, IconButton, Divider, alpha, useTheme,
} from '@mui/material';
import { Close, CardMembership } from '@mui/icons-material';
import PartnerSubscriptionPlanManager from '../partners/PartnerSubscriptionPlanManager';
import { zonePartnerPayout, payoutModelLabel } from '../../utils/partnerZoneUtils';
import { PARTNER_PLAN_NOTE_SX } from '../../constants/partnerSubscriptionPlanConfig';
import type { OperationalZoneRow } from '../../services/zoneService';

interface Props {
  open: boolean;
  zone: OperationalZoneRow | null;
  onClose: () => void;
}

export default function ZonePartnerSubscriptionsDrawer({ open, zone, onClose }: Props) {
  const theme = useTheme();
  if (!zone) return null;

  const payout = zonePartnerPayout(zone.metadata as Record<string, unknown> | undefined);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 640 } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            p: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <CardMembership color="primary" />
              <Typography variant="h6" fontWeight={800}>Partner subscriptions</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {zone.name} · {zone.code}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Earning model: <strong>{payoutModelLabel(payout.partnerPayoutModel)}</strong>
              {(payout.partnerPayoutModel === 'commission' || payout.partnerPayoutModel === 'both') && (
                <> · Commission {payout.commissionRate}%</>
              )}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          <PartnerSubscriptionPlanManager
            zoneId={zone._id}
            zoneName={zone.name}
            zoneCode={zone.code}
            compact
            showHeader
          />
        </Box>

        <Divider />
        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.06), borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={PARTNER_PLAN_NOTE_SX}>
            Use <strong>Add plan</strong> or the header ⋮ menu → <strong>Add subscription plan</strong>, then click the <strong>Partners</strong> count or row ⋮ → <strong>View subscribed partners</strong> to see plan details and subscribed partners.
          </Typography>
        </Box>
      </Stack>
    </Drawer>
  );
}
